import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaGamepad, FaHome } from 'react-icons/fa';
import { IoAlertCircleOutline } from 'react-icons/io5';

export default function NotFound() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#07080F] flex items-center justify-center relative overflow-hidden font-sans">
            {/* Ambient Background Glows */}
            <div className="absolute top-1/4 left-1/4 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#6263ED]/10 rounded-full blur-[120px] animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#A7006F]/10 rounded-full blur-[120px] animate-pulse [animation-delay:1s]" />

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative z-10 text-center px-4 max-w-xl w-full"
            >
                {/* 404 Large Header */}
                <div className="relative mb-6 sm:mb-8 select-none">
                    <h1 className="text-[120px] sm:text-[180px] font-black text-transparent bg-clip-text bg-gradient-to-r from-[#6263ED] to-[#A7006F] leading-none tracking-tighter opacity-20 filter drop-shadow-[0_0_50px_rgba(98,99,237,0.3)]">
                        404
                    </h1>

                    <motion.div
                        initial={{ y: -5 }}
                        animate={{ y: 5 }}
                        transition={{ repeat: Infinity, duration: 2, repeatType: "reverse", ease: "easeInOut" }}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-5xl sm:text-7xl text-white/80"
                    >
                        <IoAlertCircleOutline className="filter drop-shadow-[0_0_20px_rgba(167,0,111,0.6)]" />
                    </motion.div>
                </div>

                {/* Card Container */}
                <div className="bg-[#12131a]/65 border border-white/10 backdrop-blur-xl rounded-[32px] sm:rounded-[40px] p-8 sm:p-12 shadow-2xl relative overflow-hidden">
                    {/* Glowing Top Border */}
                    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#6263ED] to-[#A7006F] opacity-80" />

                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 tracking-wide">
                        PAGE NOT FOUND
                    </h2>

                    <p className="text-gray-400 text-sm sm:text-base mb-8 leading-relaxed max-w-md mx-auto">
                        The objective was not found. The page you're seeking has either been relocated or ceased to exist in this game space.
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => navigate('/')}
                            className="px-6 py-3.5 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl bg-gradient-primary text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-[#6263ED]/25 transition-all cursor-pointer"
                        >
                            <FaHome /> Respawn at Home
                        </motion.button>
                        
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => navigate(-1)}
                            className="px-6 py-3.5 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 text-white font-bold flex items-center justify-center gap-2 hover:bg-white/10 transition-all cursor-pointer"
                        >
                            <FaGamepad /> Back to Safety
                        </motion.button>
                    </div>
                </div>

                {/* Footer Decorator */}
                <div className="mt-8 sm:mt-12 flex items-center justify-center gap-4 sm:gap-6 opacity-30">
                    <FaGamepad size={24} className="text-white" />
                    <div className="h-4 w-px bg-white/30" />
                    <span className="font-bold text-white tracking-[0.25em] text-xs uppercase">YOYO PLAYGROUND</span>
                </div>
            </motion.div>
        </div>
    );
}